/**
 * @ngdoc controller
 * @name UploadController
 * @description
 * This component renders the Upload section.
 *
 * @memberof upload
 */
class UploadController {
  /**
   * Class constructor
   * @param {Service} $location         Used to redirect user after the upload
   *                                    process finished.
   * @param {Object}  $rootScope        To broadcast the tracking events.
   * @param {Service} $q                To hadle multiple promises.
   * @param {Service} campaignsService  To get the list of campaigns.
   * @param {Service} communityService  To refresh the list of community media.
   * @param {Service} fileUtils         To get the limit of the filesize per upload.
   * @param {Service} errorHandler      To show any errors.
   * @param {Service} profileService    To refresh the list of profile media.
   * @param {Service} uploadQueue       We're going to need this to allow upload
   *                                    media on this section.
   * @param {Object}  MEDIA_STATUSES    Used to set the status of the media on the queue.
   * @param {Object}  TRACKING_EVENTS   To get the name of the events to be broadcasted.
   * @ignore
   */
  constructor(
    $location,
    $rootScope,
    $q,
    campaignsService,
    communityService,
    fileUtils,
    errorHandler,
    profileService,
    uploadQueue,
    MEDIA_STATUSES,
    TRACKING_EVENTS,
  ) {
    'ngInject';

    /**
     * The local reference to the `$location` service.
     * @type {Service}
     */
    this.$location = $location;
    /**
     * The local reference to the `$rootScope` object.
     * @type {Object}
     */
    this.$rootScope = $rootScope;
    /**
     * The local reference to the `$location` service.
     * @type {Service}
     */
    this.$q = $q;
    /**
     * The local reference to the `campaignsService` service.
     * @type {Service}
     */
    this.campaignsService = campaignsService;
    /**
     * The local reference to the `communityService` service.
     * @type {Service}
     */
    this.communityService = communityService;
    /**
     * The local reference to the `fileUtils` service.
     * @type {Service}
     */
    this.fileUtils = fileUtils;
    /**
     * The local reference to the `errorHandler` service.
     * @type {Object}
     */
    this.errorHandler = errorHandler;
    /**
     * The local reference to the `profileService` service.
     * @type {Service}
     */
    this.profileService = profileService;
    /**
     * The local reference to the `uploadQueue` service.
     * @type {Service}
     */
    this.uploadQueue = uploadQueue;
    /**
     * The local reference to the media statuses object.
     * @type {Object}
     */
    this.mediaStatus = MEDIA_STATUSES;
    /**
     * The local reference to the `TRACKING_EVENTS` constant.
     * @type {Object}
     */
    this.TRACKING_EVENTS = TRACKING_EVENTS;
    /**
     * A list of status which represent media can't be previsualized
     * in the section.
     * @type {Array}
     */
    this.previewErrorStatus = [
      this.mediaStatus.previewFail,
      this.mediaStatus.invalidImage,
      this.mediaStatus.invalidSize,
    ];
    /**
     * The local reference to the `campaigns` array.
     * @type {?Array}
     */
    this.campaigns = null;
    /**
     * The local reference to the `campaign` object.
     * @type {Null|Object}
     */
    this.campaign = null;
    /**
     * To show the progress bar when the user start the submission of the media.
     * @type {Boolean}
     */
    this.uploading = false;
    /**
     * To show media in error.
     * @type {Object}
     */
    this.mediaError = {};
  }
  /**
   * Initialize variables.
   */
  $onInit() {
    this.campaignsService.getCampaigns().then((campaigns) => {
      this.campaigns = campaigns;

      if (this.campaignId) {
        this.campaign = campaigns.find((campaign) => campaign.id === this.campaignId);
      }
    });

    // Init the mediaError map
    this._generateMediaErrorMap();
  }
  /**
   * Generate the media error map.
   * @param {Object} validate Should validate each media.
   */
  _generateMediaErrorMap(validate) {
    this.uploadQueue.queue.forEach((media) => {
      this.mediaError[media.id] = validate ?
        !media.campaign.id :
        false;
    });
  }
  /**
   * Return all the media in the queue with status OK and a campaign selected.
   * @return {Array}
   */
  _getValidatedMedia() {
    return this.uploadQueue.getValidMediaInQueue()
    .filter((media) => this.mediaError[media.id] === false);
  }
  /**
   * Derterminate if the media has a failied preview status.
   * @param  {Object}  media The media to evaluate.
   * @return {Boolean}
   */
  failedPreview(media) {
    return this.previewErrorStatus.includes(media.status);
  }
  /**
   * Add the FILE oject to the upload queue service.
   * @param {FileList} selectedFiles list of seleted files by the user.
   */
  fileSelected(selectedFiles) {
    this.uploadQueue.addItems(selectedFiles, this.campaign)
    .then(this._generateMediaErrorMap.bind(this))
    .catch((err) => this.errorHandler.handleError(err));
  }
  /**
   * Send the list of media which can be saved by the service.
   * Then redirect to `/me`
   */
  finishUpload() {
    this._generateMediaErrorMap(true);

    let mediaToSave = this._getValidatedMedia();

    // Remove outOflimits media if necessary
    const outOflimits = this.uploadQueue.outOflimits();
    if (outOflimits) {
      mediaToSave = mediaToSave.slice(0, mediaToSave.length - outOflimits);
    }

    if (mediaToSave.length > 0) {
      this.trackClick('Finish');

      this.uploading = true;
      // Filter the unique campaigns
      const selectedCampaigns = mediaToSave.map((media) => media.campaign)
      .filter((campaign, index, self) => self.findIndex((c) => c.id === campaign.id) === index);

      this.uploadQueue.finishUpload(mediaToSave)
      .then((responses) => {
        if (!responses.some((response) => response.error)) {
          /**
           * Refresh the media only of the selected campaigns,
           * the community and the profile.
           */
          const promises = [
            ...selectedCampaigns.map((campaign) => campaign.getMedia(true)),
            this.communityService.getMedia(true),
            this.profileService.getMedia(true),
          ];

          this.$q.all(promises).then(() => {
            if (this.uploadQueue.queue.length === 0) {
              this.$location.url('/profile');
            }
          })
          .catch((err) => this.errorHandler.handleError(err))
          .finally(() => {
            this.uploading = false;
          });
        } else {
          this.errorHandler.showErrorMessage('Some media could not be uploaded');
          this.uploading = false;
        }
      })
      .catch((err) => this.errorHandler.handleError(err));
    }
  }
  /**
   * Hanlde when a campaign is selected in the dropdown.
   * @param  {Object} media    the media to be changed.
   * @param  {Object} campaign the campaign to be selected.
   */
  onItemSelected(media, campaign) {
    media.campaign = campaign;
    this.mediaError[media.id] = false;
  }
  /**
   * Remove a media from the upload queue.
   * @param  {Object} media the media to be removed.
   */
  removeMediaFromQueue(media) {
    this.uploadQueue.removeItem(media);
    this.trackClick('Delete');
  }
  /**
   * The finish link should be enabled only when there's something to send.
   * @return {Boolean} when the service is not working and the
   *                   queue have something to valid to send.
   */
  isFinishDisabled() {
    return this.uploading
            || this.uploadQueue.isWorking
            || !this._getValidatedMedia().length;
  }
  /**
   * Track the click event.
   * @param {String} type the type of click event to track.
   */
  trackClick(type) {
    this.$rootScope.$broadcast(this.TRACKING_EVENTS.upload[`click${type}`]);
  }
}

export default UploadController;



// WEBPACK FOOTER //
// ./src/frontend/modules/upload/components/upload/upload.controller.js